import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from "@angular/core";
import {
    Calendar,
    CalendarEventDetails,
    CalendarService,
    CalendarWidget,
    CalendarWidgetField,
    FieldGroup,
    WidgetType,
} from "./calendaring.service";
import { EventI, EventsI, EventProfileReservation } from "../../pojo/EventI";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { CalendaringUtil, UxConfig } from "./calendaring.util";

@TypeManagerDecorator("s25-ng-calendar-table")
@Component({
    selector: "s25-ng-calendar-table",
    template: `
        @if (init) {
            @if (!eventDetails) {
                @if (loading) {
                    <s25-ng-loading-inline-static></s25-ng-loading-inline-static>
                }
                @if (calendar.subcalendars?.length) {
                    <div class="c-padding-top--single c-padding-bottom--single">
                        @for (subcalendar of calendar.subcalendars; track subcalendar.calendarId) {
                            <s25-ng-checkbox
                                [style.background]="subcalendar.backgroundGradient"
                                class="subcalendar-checkbox"
                                [(value)]="subcalendar.selected"
                                (valueChange)="subCalendarChange()"
                            >
                                {{ subcalendar.displayName }}
                            </s25-ng-checkbox>
                        }
                        <div>
                            <s25-ng-button [type]="'flat'" [onClick]="selectAll">All</s25-ng-button>
                            <s25-ng-button [type]="'none'" [onClick]="selectNone">None</s25-ng-button>
                        </div>
                    </div>
                }

                <table class="event-table">
                    <tbody>
                        @for (group of groups; track group.name) {
                            <tr class="group-row">
                                <td
                                    [style.background-color]="uxConfig.groupBackgroundColor"
                                    [style.color]="uxConfig.groupColor"
                                    [style.font-weight]="uxConfig.groupFontWeight"
                                    [style.border-top]="uxConfig.borderWidth + 'px solid ' + uxConfig.borderColor"
                                    [attr.colspan]="columns.length"
                                >
                                    {{ group.name }}
                                </td>
                            </tr>
                            <tr>
                                @for (column of columns; track column.widgetFieldId) {
                                    <th
                                        [style.background-color]="uxConfig.headerBackgroundColor"
                                        [style.color]="uxConfig.headerColor"
                                    >
                                        {{ column.label }}
                                    </th>
                                }
                            </tr>
                            @for (reservation of group.reservations; track reservation.reservation_id) {
                                <tr>
                                    @for (column of columns; track column.widgetFieldId) {
                                        @let columnValue =
                                            getColumnValue(calendar, widget, column, reservation.event, reservation);
                                        <td
                                            class="{{ columnValue.class }}"
                                            [style.border-top]="
                                                uxConfig.borderWidth + 'px solid ' + uxConfig.borderColor
                                            "
                                        >
                                            <s25-ng-calendar-column-value
                                                [columnValue]="columnValue"
                                                [reservation]="reservation"
                                                [switchToEventDetails]="switchToEventDetails"
                                            ></s25-ng-calendar-column-value>
                                        </td>
                                    }
                                </tr>
                            }
                        }
                    </tbody>
                </table>
            } @else {
                <s25-ng-calendar-event-details [eventDetails]="eventDetails" (close)="closeEventDetails()">
                </s25-ng-calendar-event-details>
            }
        }
    `,
    styles: [
        `
            .subcalendar-checkbox {
                padding-left: 0.625rem;
                padding-top: 7px;
                padding-bottom: 7px;
                padding-right: 0.625rem;
            }
            .event-table {
                width: 100%;
                border-collapse: collapse;
            }
            .event-table th,
            .event-table td {
                padding: 8px;
            }
            .calDateRange {
                font-weight: bold;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class CalendarTable implements OnInit {
    constructor(public cd: ChangeDetectorRef) {}

    @Input() calendar?: Calendar;
    @Input() uxConfig?: UxConfig;
    @Input() startDt?: Date;
    @Input() endDt?: Date;

    @Input() events?: EventsI; // useful input for easy testing

    init = false;
    loading = false;
    widget: CalendarWidget;
    columns: CalendarWidgetField[] = [];
    groups: FieldGroup[] = [];
    eventDetails: CalendarEventDetails;

    selectAll = async () => {
        this.calendar.subcalendars.forEach((sc) => (sc.selected = true));
        await this.refresh();
    };

    selectNone = async () => {
        this.calendar.subcalendars.forEach((sc) => (sc.selected = false));
        await this.refresh();
    };

    refresh = async () => {
        this.loading = true;
        this.cd.detectChanges();
        this.events = await this.getEvents();
        this.groups = CalendaringUtil.groupReservations(this.events, this.calendar, this.widget);
        this.loading = false;
        this.cd.detectChanges();
    };

    subCalendarChange = S25Util.debounceTs(() => {
        return this.refresh();
    }, 1000);

    getEvents = async () => {
        const calendarIds = new Set(this.calendar.subcalendars.filter((sc) => sc.selected).map((sc) => sc.calendarId));
        return CalendarService.getEvents(Array.from(calendarIds), this.startDt, this.endDt);
    };

    getColumnValue(
        calendar: Calendar,
        widget: CalendarWidget,
        column: CalendarWidgetField,
        event: EventI,
        reservation: EventProfileReservation,
    ) {
        return CalendaringUtil.getColumnValue(calendar, widget, column, event, reservation);
    }

    closeEventDetails() {
        this.eventDetails = null;
        this.cd.detectChanges();
    }

    switchToEventDetails = (reservation: EventProfileReservation) => {
        this.eventDetails = {
            calendar: this.calendar,
            parentWidget: this.widget,
            reservation: reservation,
        };
        this.cd.detectChanges();
    };

    async ngOnInit() {
        if (this.calendar) {
            this.startDt = this.startDt || S25Util.date.currentDate();
            this.endDt = this.endDt || S25Util.date.addDays(this.startDt, 7);

            this.widget = this.calendar.widgets.find((widget) => widget.widgetType === WidgetType.table);
            if (!this.widget) {
                console.error(`Widget [${WidgetType.table}] not found.`);
                this.widget = {};
            }
            this.columns = [...(this.widget.widgetFields || [])].sort(
                (a, b) => (a.widgetFieldId ?? 0) - (b.widgetFieldId ?? 0),
            );
            this.uxConfig = this.uxConfig || CalendaringUtil.resolveUxConfigs(this.calendar, this.widget);

            this.events = this.events || (await this.getEvents());
            this.groups = CalendaringUtil.groupReservations(this.events, this.calendar, this.widget);

            this.init = true;
            this.cd.detectChanges();
        }
    }
}
